/**
 * Sync Voyado Elevate session keys to Shopify cart attributes.
 * Required by the Elevate Shopify app for cart-page recommendations and order attribution.
 */
(function () {
  const ATTRIBUTE_KEYS = {
    customerKey: '_elevate_customer_key',
    sessionKey: '_elevate_session_key',
    market: '_elevate_market',
    locale: '_elevate_locale'
  };

  let updating = false;

  function hasElevateAttributes(attributes) {
    return (
      attributes?.[ATTRIBUTE_KEYS.customerKey] &&
      attributes?.[ATTRIBUTE_KEYS.sessionKey] &&
      attributes?.[ATTRIBUTE_KEYS.market] &&
      attributes?.[ATTRIBUTE_KEYS.locale]
    );
  }

  function readLocale(client) {
    return client?.locale || window.ElevateThemeConfig?.locale || '';
  }

  function readFromElevateApp() {
    const client = window.elevate?.query?.c;

    if (!client || typeof client.session !== 'function') {
      return null;
    }

    const { customerKey, sessionKey } = client.session();
    const market = client.market;
    const locale = readLocale(client);

    if (!customerKey || !sessionKey || !market || !locale) {
      return null;
    }

    return { customerKey, sessionKey, market, locale };
  }

  function readFromThemeFallback() {
    const core = window.ElevateCore;

    if (!core) {
      return null;
    }

    const market = window.ElevateThemeConfig?.market;
    const locale = window.ElevateThemeConfig?.locale;

    if (!market || !locale) {
      return null;
    }

    const { customerKey, sessionKey } = core.readSession();

    if (!customerKey || !sessionKey) {
      return null;
    }

    return { customerKey, sessionKey, market, locale };
  }

  async function waitForElevateApp(maxAttempts = 30, delayMs = 100) {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const context = readFromElevateApp();

      if (context) {
        return context;
      }

      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    return null;
  }

  async function resolveElevateContext() {
    return (await waitForElevateApp(5, 0)) ?? readFromElevateApp() ?? readFromThemeFallback();
  }

  async function syncElevateCartAttributes(cart) {
    if (updating) {
      return;
    }

    const cartData =
      cart ??
      (await fetch(`${Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' }
      }).then((response) => response.json()));

    if (hasElevateAttributes(cartData.attributes)) {
      return;
    }

    const context = await resolveElevateContext();

    if (!context) {
      return;
    }

    updating = true;

    try {
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            [ATTRIBUTE_KEYS.customerKey]: context.customerKey,
            [ATTRIBUTE_KEYS.sessionKey]: context.sessionKey,
            [ATTRIBUTE_KEYS.market]: context.market,
            [ATTRIBUTE_KEYS.locale]: context.locale
          }
        })
      });

      if (!response.ok) {
        return;
      }

      const updatedCart = await response.json();

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'elevate:cart-sync',
            cart: updatedCart
          }
        })
      );
    } finally {
      updating = false;
    }
  }

  document.addEventListener('cart:change', (event) => {
    const cart = event.detail?.cart;

    if (!cart) {
      return;
    }

    syncElevateCartAttributes(cart);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      syncElevateCartAttributes();
    });
  } else {
    syncElevateCartAttributes();
  }

  window.addEventListener('load', () => {
    syncElevateCartAttributes();
  });
})();
